import { useEffect, useLayoutEffect, useRef } from 'react';

/* The mark, expanded into a scene: two finished cubes, one still being drawn,
   and a couple of small ones drifting. Same 30° grid as the logo, same colours,
   soft gradient faces like the logo's own rendering. The dashed cube's edges
   crawl forever and never complete — that cube is the company.

   This scene no longer assembles into the logo itself: that set piece now
   belongs to Assembly.tsx, further down the page. What's left here is the
   hero's own idle world — first-visit intro, a per-cube float, pointer
   parallax, the glow, and a plain fade as the hero scrolls out of view (a
   pure function of how far the hero's own top edge has scrolled, so it's
   reversible scrolling back up) so the scene never lingers into the section
   below it. */

const EX = [0.866, -0.5];
const EY = [-0.866, -0.5];
const EZ = [0, -1];

type P2 = [number, number];

function pt(o: P2, a: number, b: number, c: number): P2 {
  return [
    o[0] + a * EX[0] + b * EY[0] + c * EZ[0],
    o[1] + a * EX[1] + b * EY[1] + c * EZ[1],
  ];
}

function pts(list: P2[]): string {
  return list.map((p) => `${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ');
}

/* o = front-bottom corner (lowest point on screen), a = edge length */
function faces(o: P2, a: number) {
  const A = o;
  const B = pt(o, a, 0, 0);
  const C = pt(o, 0, a, 0);
  const T = pt(o, 0, 0, a);
  const TR = pt(o, a, 0, a);
  const TL = pt(o, 0, a, a);
  const TB = pt(o, a, a, a);
  return {
    left: pts([A, C, TL, T]),
    right: pts([A, B, TR, T]),
    top: pts([T, TR, TB, TL]),
    edges: { A, B, C, T, TR, TL, TB },
  };
}

const Cube = ({ o, a, tone, cls }: { o: P2; a: number; tone: string; cls: string }) => {
  const f = faces(o, a);
  return (
    <g className={cls}>
      <polygon points={f.left} fill={`url(#bw-${tone}-l)`} stroke="var(--cube-line)" strokeWidth="1" strokeLinejoin="round" />
      <polygon points={f.right} fill={`url(#bw-${tone}-r)`} stroke="var(--cube-line)" strokeWidth="1" strokeLinejoin="round" />
      <polygon points={f.top} fill={`url(#bw-${tone}-t)`} stroke="var(--cube-line)" strokeWidth="1" strokeLinejoin="round" />
    </g>
  );
};

const DashedCube = ({ o, a, cls }: { o: P2; a: number; cls: string }) => {
  const f = faces(o, a);
  const { A, B, C, T, TR, TL, TB } = f.edges;
  const edge = (p1: P2, p2: P2, crawl: boolean, key: string) => (
    <line
      key={key}
      x1={p1[0].toFixed(1)}
      y1={p1[1].toFixed(1)}
      x2={p2[0].toFixed(1)}
      y2={p2[1].toFixed(1)}
      stroke="var(--dash-edge)"
      strokeWidth="2.5"
      strokeDasharray="9 7"
      strokeLinecap="round"
      className={crawl ? 'crawl' : undefined}
    />
  );
  /* every edge crawls, and the endpoint order makes the flow coherent:
     one rotation around the silhouette (A→B→TR→TB→TL→C→A), spokes flowing
     through the centre the same way */
  return (
    <g className={cls}>
      <polygon points={f.top} fill="var(--dash-top)" opacity="0.14" />
      {edge(A, B, true, 'ab')}
      {edge(B, TR, true, 'btr')}
      {edge(TR, TB, true, 'trtb')}
      {edge(TB, TL, true, 'tbtl')}
      {edge(TL, C, true, 'tlc')}
      {edge(C, A, true, 'ca')}
      {edge(A, T, true, 'at')}
      {edge(T, TR, true, 'ttr')}
      {edge(T, TL, true, 'ttl')}
    </g>
  );
};

const Grad = ({ id, from, to, vertical }: { id: string; from: string; to: string; vertical?: boolean }) => (
  <linearGradient id={id} x1="0" y1="0" x2={vertical ? '0' : '1'} y2={vertical ? '1' : '0.6'}>
    <stop offset="0" stopColor={from} />
    <stop offset="1" stopColor={to} />
  </linearGradient>
);

/* The hero's four cubes at rest, scattered — same positions Assembly.tsx's
   set piece starts its own copies from (its MORPH map's `o`), so the two
   scenes read as the same objects continuing, not a coincidence. This file
   itself never travels them anywhere: it only floats each in place. */
const BOB: Record<string, { period: number; phase: number }> = {
  mint: { period: 7.5, phase: 0 },
  peri: { period: 9.0, phase: 2.1 },
  ice: { period: 8.2, phase: 4.4 },
  dash: { period: 6.5, phase: 1.2 },
};
const BOB_AMPLITUDE = 9;

function clamp01(v: number): number {
  if (v < 0) return 0;
  if (v > 1) return 1;
  return v;
}

const Scene = () => {
  const ref = useRef<SVGSVGElement>(null);

  /* first visit of a session, starting at the top: the world builds itself.
     Never runs under reduced motion; if storage is unavailable, no intro,
     and nothing is ever left hidden because `backwards` fill only exists
     while the .intro class does. */
  useLayoutEffect(() => {
    const svg = ref.current;
    if (!svg) return;
    const hero = svg.closest<HTMLElement>('.hero');
    if (!hero) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (window.scrollY > 40) return;
    try {
      if (sessionStorage.getItem('bw-intro')) return;
      sessionStorage.setItem('bw-intro', '1');
    } catch {
      return;
    }
    hero.classList.add('intro');
    const tm = setTimeout(() => hero.classList.remove('intro'), 2600);
    return () => clearTimeout(tm);
  }, []);

  /* float + hero-exit fade, one continuous loop while the scene is on
     screen. The bob lives here, not in CSS, so each cube keeps its own
     period and phase (CSS floats with independent phases and no shared
     clock were the old float-a..d classes, since removed). The fade is a
     pure function of how far the hero's own top edge has scrolled past the
     viewport top — not window.scrollY on its own, so this stays correct
     even if something above the hero (a banner, a taller nav) ever changes
     the hero's offset from the document top. */
  useEffect(() => {
    const svg = ref.current;
    if (!svg) return;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const hero = svg.closest<HTMLElement>('.hero');
    if (!hero) return;
    const parts = Array.from(svg.querySelectorAll<SVGGElement>('g[data-float]'));
    let raf = 0;
    let running = false;

    const frame = () => {
      raf = running ? requestAnimationFrame(frame) : 0;
      if (mq.matches) return;
      const now = performance.now() / 1000;
      for (const g of parts) {
        const b = BOB[g.dataset.float as string];
        if (!b) continue;
        const bob = Math.sin((now / b.period) * Math.PI * 2 + b.phase) * BOB_AMPLITUDE;
        g.setAttribute('transform', `translate(0 ${bob.toFixed(2)})`);
      }
      /* fades over the LAST 35% of the hero's own height: heroRect.top is
         negative once the hero has scrolled past the viewport's top edge,
         so -heroRect.top is how far it has scrolled; that distance divided
         by the hero's height is scroll-through fraction, 0 at the top of
         the page, 1 once the hero's top has scrolled a full hero-height
         past the viewport top. */
      const heroHeight = hero.offsetHeight || 1;
      const heroRect = hero.getBoundingClientRect();
      const scrolledFrac = clamp01(-heroRect.top / heroHeight);
      const fadeStart = 0.65;
      const opacity = 1 - clamp01((scrolledFrac - fadeStart) / (1 - fadeStart));
      svg.style.opacity = opacity.toFixed(3);
    };
    const start = () => {
      if (!running) {
        running = true;
        raf = requestAnimationFrame(frame);
      }
    };
    const stop = () => {
      running = false;
    };
    const io = new IntersectionObserver(([e]) => (e.isIntersecting ? start() : stop()), {
      rootMargin: '200px 0px 200px 0px',
    });
    io.observe(svg);
    const onPref = () => {
      if (!mq.matches) return;
      for (const g of parts) g.removeAttribute('transform');
      svg.style.opacity = '';
    };
    mq.addEventListener('change', onPref);
    start();
    return () => {
      stop();
      io.disconnect();
      mq.removeEventListener('change', onPref);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  /* the scene rests when it scrolls out of view */
  useEffect(() => {
    const svg = ref.current;
    if (!svg) return;
    const io = new IntersectionObserver(([entry]) => {
      svg.classList.toggle('scene--rest', !entry.isIntersecting);
    });
    io.observe(svg);
    return () => io.disconnect();
  }, []);

  /* pointer parallax — transforms only, no React state, no scroll listeners.
     Honors reduced-motion live, not just at mount. */
  useEffect(() => {
    const svg = ref.current;
    if (!svg) return;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const hero = svg.closest('.hero');
    if (!hero) return;

    const groups = Array.from(svg.querySelectorAll<SVGGElement>('g[data-depth]'));
    let tx = 0;
    let ty = 0;
    let cx = 0;
    let cy = 0;
    let raf = 0;

    const step = () => {
      cx += (tx - cx) * 0.08;
      cy += (ty - cy) * 0.08;
      for (const g of groups) {
        const d = Number(g.dataset.depth);
        g.style.translate = `${(cx * d).toFixed(2)}px ${(cy * d).toFixed(2)}px`;
      }
      if (Math.abs(tx - cx) > 0.001 || Math.abs(ty - cy) > 0.001) raf = requestAnimationFrame(step);
      else raf = 0;
    };
    const onMove = (e: PointerEvent) => {
      if (mq.matches) return;
      const r = svg.getBoundingClientRect();
      tx = (e.clientX - r.left) / r.width - 0.5;
      ty = (e.clientY - r.top) / r.height - 0.5;
      if (!raf) raf = requestAnimationFrame(step);
    };
    const onPref = () => {
      if (!mq.matches) return;
      tx = 0;
      ty = 0;
      for (const g of groups) g.style.translate = '0px 0px';
    };
    hero.addEventListener('pointermove', onMove as EventListener, { passive: true });
    mq.addEventListener('change', onPref);
    return () => {
      hero.removeEventListener('pointermove', onMove as EventListener);
      mq.removeEventListener('change', onPref);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <svg
      ref={ref}
      className="scene"
      viewBox="0 0 560 520"
      role="img"
      aria-label="The Blockworks mark as a living scene: two finished cubes and a third still being drawn"
    >
      <defs>
        <Grad id="bw-peri-t" from="#eef3fd" to="#d3ddf5" />
        <Grad id="bw-peri-r" from="#c6d0ec" to="#a9b7e0" vertical />
        <Grad id="bw-peri-l" from="#aab8de" to="#8c9cce" vertical />
        <Grad id="bw-mint-t" from="#ddf0f5" to="#c2e2ec" />
        <Grad id="bw-mint-r" from="#bfe1ec" to="#9ccbdc" vertical />
        <Grad id="bw-mint-l" from="#a4cfdd" to="#82b4c6" vertical />
        <Grad id="bw-lilac-t" from="#f6efff" to="#e6d8fb" />
        <Grad id="bw-lilac-r" from="#dccbf5" to="#c8b3ea" vertical />
        <Grad id="bw-lilac-l" from="#cbb7ec" to="#b09ad9" vertical />
        <Grad id="bw-ice-t" from="#f0f5fe" to="#dfe9f9" />
        <Grad id="bw-ice-r" from="#d3e0f5" to="#bccfef" vertical />
        <Grad id="bw-ice-l" from="#c0cfea" to="#a8bce0" vertical />
        <radialGradient id="bw-glow-a">
          <stop offset="0" stopColor="#8b78be" stopOpacity="0.5" />
          <stop offset="1" stopColor="#8b78be" stopOpacity="0" />
        </radialGradient>
        <radialGradient id="bw-glow-b">
          <stop offset="0" stopColor="#5c9bb0" stopOpacity="0.5" />
          <stop offset="1" stopColor="#5c9bb0" stopOpacity="0" />
        </radialGradient>
      </defs>

      <g data-glow="true">
        <circle cx="300" cy="240" r="230" fill="url(#bw-glow-a)" style={{ opacity: 'var(--glow-op)' as never }} />
        <circle cx="180" cy="340" r="170" fill="url(#bw-glow-b)" style={{ opacity: 'var(--glow-op)' as never }} />
      </g>

      {/* the four main cubes carry no CSS float: their bob is computed in
          the loop above so every cube keeps its own period and phase */}
      <g data-depth="10">
        <g data-float="ice">
          <Cube o={[430, 150]} a={34} tone="ice" cls="" />
        </g>
      </g>
      <g data-depth="4">
        <g data-float="peri">
          <Cube o={[188, 300]} a={96} tone="peri" cls="" />
        </g>
      </g>
      <g data-depth="10" data-fade="true">
        <Cube o={[90, 210]} a={26} tone="mint" cls="float-e" />
      </g>
      <g data-depth="7">
        <g data-float="mint">
          <Cube o={[190, 470]} a={108} tone="mint" cls="" />
        </g>
      </g>
      <g data-depth="8">
        <g data-float="dash">
          <DashedCube o={[368, 476]} a={126} cls="" />
        </g>
      </g>
    </svg>
  );
};

export default Scene;
